import * as Sentry from "@sentry/gatsby";

const AUTHENTICATION_ERRORS = ['expired_token', 'invalid_authorization_header', 'not_authorized'];
const USER_FACING_ERRORS = ['validation', 'not_found', 'not_authorized', 'one_time_password_required', 'not_authenticated'];
const OPERATIONS_TO_RETHROW = ['CrawlSite'];

class ErrorBuilder {
  constructor(operation) {
    this.operation = operation;
    this.baseDetails = {
      operationName: operation?.operationName,
      variables: operation?.variables,
      timestamp: new Date().toISOString()
    };
  }

  buildNetworkError(originalError) {
    const category = 'NetworkError';
    const message = `[${category}]: ${originalError}`;
    const reason = originalError.message;

    return {
      category,
      rethrow: false,
      userFacing: false,
      message,
      details: {
        ...this.baseDetails,
        reason,
        originalError
      }
    };
  }

  buildGraphQLError(originalError) {
    const { message, locations, path } = originalError;
    const lowerMessage = message.toLowerCase();
    const isAuthError = AUTHENTICATION_ERRORS.includes(lowerMessage);
    const isUserFacing = USER_FACING_ERRORS.some(type => lowerMessage.includes(type));
    const isOperationToRethrow = OPERATIONS_TO_RETHROW.includes(this.operation?.operationName);
    const category = isAuthError ? 'AuthenticationError' : 'GraphQLError';
    const wrappedMessage = `[${category}]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`;

    return {
      category,
      rethrow: isOperationToRethrow,
      userFacing: isUserFacing,
      message: wrappedMessage,
      details: {
        ...this.baseDetails,
        reason: lowerMessage,
        originalError
      }
    };
  }
}

const writeToConsole = (error, level = 'debug') => console[level](error);

const captureError = (error) => {
  const { category, message, details, rethrow, userFacing } = error;

  const simpleError = new Error(message);

  writeToConsole(simpleError, 'error');

  if (!userFacing) {
    Sentry.captureException(simpleError, {
      tags: {
        errorCategory: category,
        operationName: details.operationName
      },
      extra: details
    });
  }

  if (rethrow) throw simpleError;
};

const buildNetworkErrorDetails = (networkError, operation) => {
  const builder = new ErrorBuilder(operation);
  return builder.buildNetworkError(networkError);
};

const buildGraphQLErrorDetails = (graphQLError, operation) => {
  const builder = new ErrorBuilder(operation);
  return builder.buildGraphQLError(graphQLError);
};

const buildUnknownError = (error) => {
  const { locations, path, extensions } = error;
  return {
    message: 'Unknown error',
    locations,
    path,
    extensions
  };
};

export {
  writeToConsole,
  captureError,
  ErrorBuilder,
  buildNetworkErrorDetails,
  buildGraphQLErrorDetails,
  buildUnknownError
};